// Datos de la pantalla Clientes del portal del vendedor.
//
// La cartera se trae COMPLETA encadenando `nextToken` y recién después se filtra, ordena y pagina
// en memoria. Es lo que permite que el contador de la barra y el rango de alta digan la verdad: con
// una sola página cargada, "12 clientes" sería "12 de los que alcancé a traer" y el rango de fechas
// escondería clientes que sí cumplen. La cartera es de UNA tienda, así que son pocas páginas.
import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import {
    getCustomer360,
    listCustomers,
    searchCustomers,
    type CustomerListFilters,
    type CustomerListItem,
} from '@/api/services/retail-customer';

/** Máximo que admite el silo: menos páginas para la misma cartera. */
const PAGE_LIMIT = 100;

/**
 * Techo de páginas encadenadas. Existe para que una cartera enorme no dispare requests sin fin;
 * al topar, la pantalla lo dice en vez de fingir que ya trajo todo.
 */
const MAX_PAGES = 20;

export interface SellerCustomersResult {
    rows: CustomerListItem[];
    /** Primera página en vuelo: todavía no hay nada que pintar. */
    isLoading: boolean;
    /** Ya hay filas, pero siguen llegando páginas. */
    isLoadingMore: boolean;
    /** Se llegó al techo de páginas y el silo seguía ofreciendo más. */
    isTruncated: boolean;
    error: Error | null;
    refetch: () => void;
}

/**
 * La cartera de la tienda del vendedor.
 *
 * 🔴 El fin de la lista lo marca la AUSENCIA de `nextToken`, nunca una página vacía: `level` y `kyc`
 * se aplican en DynamoDB después del límite, así que una página filtrada puede venir sin ítems y
 * tener más páginas detrás. Cortar por `items.length === 0` perdería clientes en silencio.
 */
export function useSellerCustomers(serverFilters: CustomerListFilters): SellerCustomersResult {
    const query = useInfiniteQuery({
        queryKey: ['retail-customer', 'list', serverFilters],
        queryFn: ({ pageParam }) =>
            listCustomers({ ...serverFilters, limit: PAGE_LIMIT, nextToken: pageParam }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.nextToken ?? undefined,
        staleTime: 60_000,
    });

    const pagesFetched = query.data?.pages.length ?? 0;
    const cappedOut = pagesFetched >= MAX_PAGES;
    const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

    useEffect(() => {
        if (hasNextPage && !isFetchingNextPage && !cappedOut) void fetchNextPage();
    }, [hasNextPage, isFetchingNextPage, cappedOut, fetchNextPage]);

    const rows = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data]);

    return {
        rows,
        isLoading: query.isPending,
        isLoadingMore: !query.isPending && hasNextPage && !cappedOut,
        isTruncated: cappedOut && hasNextPage,
        error: asError(query.error),
        refetch: () => {
            void query.refetch();
        },
    };
}

/**
 * La búsqueda exacta del silo, que es de alcance TENANT: encuentra al cliente aunque sea de otra
 * tienda. Solo corre con un teléfono o una CURP completos — con cualquier otra cosa el silo
 * devuelve `[]`, así que la llamada se ahorra.
 */
export function useExactCustomerSearch(term: string | null) {
    return useQuery({
        queryKey: ['retail-customer', 'search', term],
        queryFn: () => searchCustomers(term ?? ''),
        enabled: term !== null,
        staleTime: 60_000,
        // Sin esto el retry por omisión de `App.tsx` reintenta dos veces con backoff todo lo que no
        // sea `ApiError` — y un contrato que derivó no se arregla reintentando.
        retry: false,
    });
}

/**
 * Ficha del cliente seleccionado, por el Cliente 360 — la única lectura de ficha que el vendedor
 * tiene permitida. Deshabilitada mientras no haya selección.
 */
export function useCustomerDetail(retailCustomerId: string | null) {
    const query = useQuery({
        queryKey: ['retail-customer', 'detail360', retailCustomerId],
        queryFn: () => getCustomer360(retailCustomerId ?? ''),
        enabled: retailCustomerId !== null,
        staleTime: 60_000,
    });
    return {
        data: query.data,
        isPending: query.isPending,
        error: asError(query.error),
        refetch: () => {
            void query.refetch();
        },
    };
}

/** El error de una query llega tipado laxo; la UI necesita un `Error` o nada. */
function asError(error: unknown): Error | null {
    if (!error) return null;
    if (error instanceof Error) return error;
    return new Error(typeof error === 'string' ? error : 'Error desconocido');
}
